import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Flex from "../flex";
import PillButton from "./PillButton";
import FilterDropdown from "./FilterDropdown";
import BottomSheet from "../bottom-sheet";
import mediaQuery from "../../utils/media-query";

const FilterBarContainer = styled(Flex)`
  width: 100%;
  padding: 16px;
  border-top: 1px solid #dee5ec;
  background: #fff;
  position: relative;
  margin: 0 auto;
  justify-content: center;
`;

const FilterContainer = styled(Flex)`
  width: 100%;
  max-width: 1080px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 0;
  gap: 8px;
`;

const PillContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const FilterBar = ({ onFilterChange }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [selectedPostType, setSelectedPostType] = useState("all");
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);

  // Mobile detection
  useEffect(() => {
    const mq = mediaQuery();
    mq.onInit(setIsMobile);
    mq.onBreakpoint(setIsMobile);

    // Cleanup function to remove listeners
    return () => {
      if (mq && typeof mq.cleanup === "function") {
        mq.cleanup();
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        // Don't close if clicking inside bottom sheet
        const isInsideBottomSheet = event.target.closest("[data-bottom-sheet]");
        if (!isInsideBottomSheet) {
          // Use functional update to avoid unnecessary re-renders and stale closure issues
          setActiveDropdown(prev => (prev !== null ? null : prev));
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePillButtonClick = dropdownType => {
    setActiveDropdown(activeDropdown === dropdownType ? null : dropdownType);
  };

  const handlePostTypeChange = value => {
    setSelectedPostType(value);
  };

  const handleSaveFilter = () => {
    const categories =
      selectedPostType === "publicassistance" ? ["publicassistance"] : [];
    onFilterChange({ categories });
    setActiveDropdown(null);
  };

  const postTypeOptions = [
    { value: "", label: "All Posts" },
    { value: "publicassistance", label: "Community Requests" }
  ];

  // Get the label for the currently selected post type
  const getSelectedPostTypeLabel = () => {
    const selectedOption = postTypeOptions.find(
      option => option.value === selectedPostType
    );
    return selectedOption ? selectedOption.label : "All Posts";
  };

  return (
    <>
      <FilterBarContainer ref={containerRef} alignItems={"center"}>
        <FilterContainer>
          <PillContainer>
            <PillButton
              label={getSelectedPostTypeLabel()}
              isActive={activeDropdown === "postTypes"}
              isSelected={selectedPostType !== "all"}
              onClick={() => handlePillButtonClick("postTypes")}
            />
            {activeDropdown === "postTypes" && !isMobile && (
              <FilterDropdown
                options={postTypeOptions}
                selectedValue={selectedPostType}
                onValueChange={handlePostTypeChange}
                onSave={handleSaveFilter}
                onClose={() => setActiveDropdown(null)}
              />
            )}
          </PillContainer>
        </FilterContainer>
      </FilterBarContainer>

      {activeDropdown === "postTypes" && isMobile && (
        <BottomSheet
          title={"Filter by category"}
          options={postTypeOptions}
          selectedValue={selectedPostType}
          onValueChange={handlePostTypeChange}
          onSave={handleSaveFilter}
          onClose={() => setActiveDropdown(null)}
        />
      )}
    </>
  );
};

FilterBar.propTypes = {
  onFilterChange: PropTypes.func.isRequired
};

export default FilterBar;
